import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { RdExpContributedService } from './rd-exp-contributed.service';

@Component({
    selector: 'gtw-rd-exp-contributed',
    templateUrl: './rd-exp-contributed.component.html',
    styleUrls: ['./rd-exp-contributed.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RdExpContributedComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal title - matches AngularJS (Add Adjustment / Edit Adjustment)
  modalTitle = "Add Adjustment";

  // Form data
  entity: any = undefined;
  sicCode: any = undefined;
  adjustmentName: string = '';
  adjustmentAmount: string = '';
  comments: string = '';

  // Dropdown data
  entityDetails: any[] = [];
  sicCodeDetails: any[] = [];

  // Edit mode data
  rdExpAdjDetails: any[] = [];
  originalRdExpAdjDetails: any[] = []; // Cache for reset without API call
  total: number = 0;

  // Component state
  isCreate = true;
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  
  // Loading states
  showFormLoader = false;
  entitiesLoading = false;
  dropdownLoading = false;

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private rdExpContributedService: RdExpContributedService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen) || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal) || {};
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    this.globalService = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp) || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = fp;
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.isCreate =
      !this.rowData.COMBINATION_KEY ||
      this.rowData.COMBINATION_KEY === '' ||
      this.rowData.COMBINATION_KEY === null;
    this.modalTitle = this.isCreate ? 'Add Adjustment' : 'Edit Adjustment';
  }

  init() {
    try {
      
      // CRITICAL: In CREATE mode, rowData might be empty/minimal, so check filterParams only
      const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
      const hasRowDataOrIsCreate = this.isCreate || (this.rowData && Object.keys(this.rowData).length > 0);
      
      if (hasFilterParams && hasRowDataOrIsCreate) {
        this.checkLockStatus();
        this.loadData();
      }
    } catch (e) {
      console.error('❌ Error in RdExpContributed init:', e);
    }
  }

  checkLockStatus() {
    const lockParams = {
      action_code: 'n10cpv',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.rdExpContributedService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      lockParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject && response.jsonObject.length > 0) {
          const lockData = response.jsonObject[0];
          
          if (lockData.LOCK_STATUS === 'Y') {
            this._snackBar.open(
              `Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`, 
              'Close', 
              { duration: 4000, panelClass: ['error-snackbar'] }
            );
            this.cancel();
            return;
          }
          
          if (lockData.SCENARIO_DESC === 'EXAM') {
            this._snackBar.open(
              'Create Adjustment is not available for selected Scenario.', 
              'Close', 
              { duration: 4000, panelClass: ['error-snackbar'] }
            );
            this.cancel();
            return;
          }
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  loadData() {
    
    if (!this.isCreate) {
      // EDIT MODE
      this.setEntityFromRowData();
      this.loadDropdownData();
      this.loadEditData();
    } else {
      // CREATE MODE
      this.loadEntityDetails();
    }
  }

  setEntityFromRowData() {
    this.entity = {
      KEY: this.rowData.COMBINATION_KEY,
      VALUE: this.extractEntityNameFromRowData()
    };
    this.entityDetails = [this.entity];
  }

  extractEntityNameFromRowData(): string {
    return this.rowData.ENTITY_NAME || 
           this.rowData.LE_NAME || 
           this.rowData.ENTITY_DESC ||
           this.rowData.COMBINATION_NAME ||
           `Entity ${this.rowData.COMBINATION_KEY}`;
  }

  loadEntityDetails() {
    
    this.entitiesLoading = true;
    
    const params = {
      action_code: 'g9oqag',
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.rdExpContributedService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.entitiesLoading = false;
        if (response && response.jsonObject) {
          this.entityDetails = response.jsonObject;
          this.cdr.detectChanges(); // Force change detection
        } else {
          console.warn('⚠️ No jsonObject in response');
        }
      },
      (error: any) => {
        this.entitiesLoading = false;
        console.error('❌ Error loading entity details:', error);
      }
    );
  }

  loadDropdownData() {
    const combinationKey = this.isCreate ? this.entity?.KEY : this.rowData.COMBINATION_KEY;
    
    if (!combinationKey) {
      console.warn('No combination key available for loading dropdown data');
      return;
    }
    
    this.dropdownLoading = true;
    
    const params = {
      action_code: 'dbty56',
      cc_key: combinationKey,
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    this.rdExpContributedService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.dropdownLoading = false;
        if (response && response.jsonObject) {
          const collection = response.jsonObject;
          
          // Filter SIC codes - first try SIC_CODE_PER_ME, then fallback to SIC_CODE
          this.sicCodeDetails = collection.filter((item: any) => item.CODE_GROUP === 'SIC_CODE_PER_ME');
          if (this.sicCodeDetails.length === 0) {
            this.sicCodeDetails = collection.filter((item: any) => item.CODE_GROUP === 'SIC_CODE');
          }
          
          // Set existing values if editing
          if (!this.isCreate) {
            this.setExistingValuesForEdit();
          }
        }
      },
      (error: any) => {
        this.dropdownLoading = false;
        console.error('Error loading dropdown data:', error);
      }
    );
  }

  setExistingValuesForEdit() {
    // Set SIC Code from rowData
    const existingSicCode = this.sicCodeDetails.find(item => 
      item.CODE_DESCRIPTION === this.rowData.SIC_CODE || 
      item.CODE_SHORT_DESC === this.rowData.SIC_CODE
    );
    this.sicCode = existingSicCode || undefined;
  }

  loadEditData() {
    if (!this.rowData.COMBINATION_KEY) {
      return;
    }

    const params = {
      action_code: 'n61lx4',
      sso_id: this.USER_SETTINGS.user?.sso_id || '',
      p_tax_year: this.filterParams.tax_year,
      p_scenario: this.filterParams.scenario,
      p_jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key,
      cc_key: this.rowData.COMBINATION_KEY,
      sic_code: this.rowData.SIC_CODE_KEY
    };

    this.rdExpContributedService.getJsonObject(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.rdExpAdjDetails = response.jsonObject.map((detail: any) => ({
            ...detail,
            'operation-type': 'U'
          }));
          
          // CRITICAL: Cache original data for reset functionality (avoid redundant API calls)
          this.originalRdExpAdjDetails = JSON.parse(JSON.stringify(this.rdExpAdjDetails));
          
          this.updateTotal();
        }
      },
      (error: any) => {
        console.error('Error loading edit data:', error);
      }
    );
  }

  onEntityChange() {
    if (this.entity && this.entity.KEY) {
      this.loadDropdownData();
    }
  }

  deleteRow(selectedRowData: any) {
    const index = this.rdExpAdjDetails.indexOf(selectedRowData);
    selectedRowData['isDeleted'] = true;
    selectedRowData['operation-type'] = 'D';
    this.rdExpAdjDetails[index] = selectedRowData;
    this.updateTotal();
  }

  updatedRow(selectedRowData: any) {
    const index = this.rdExpAdjDetails.indexOf(selectedRowData);
    selectedRowData['operation-type'] = 'U';
    this.rdExpAdjDetails[index] = selectedRowData;
  }

  updateTotal() {
    this.total = 0;
    this.rdExpAdjDetails.forEach((list: any) => {
      if (list['operation-type'] !== 'D') {
        this.total += parseInt(list.AMOUNT ? list.AMOUNT : 0);
      }
    });
  }

  settingAdjustmentAmount(event: any) {
    if (this.adjustmentAmount) {
      this.adjustmentAmount = this.adjustmentAmount.replace(/(?!^-)[^0-9\.]/g, '');
      const amount = this.adjustmentAmount;
      
      if (amount.indexOf('.') !== -1) {
        const amountSp = amount.split('.');
        let bPre = amountSp[0];
        let aPre = amountSp[1];
        
        if (bPre && bPre.length > 20) {
          bPre = bPre.substring(0, 19);
        }
        if (aPre && aPre.length > 2) {
          aPre = aPre.substring(0, 2);
        }
        this.adjustmentAmount = bPre + "." + aPre;
      } else if (amount.length > 20) {
        this.adjustmentAmount = amount.substring(0, amount.length - 1);
      }
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    let returnObj: any = {};
    let transactionDetails: any[] = [];
    let errorInfo = '';
    const otherInfo = {
      sso_id: this.USER_SETTINGS.user?.sso_id || '',
      SCREEN_KEY: this.globalService.screen_key,
      tax_year: this.filterParams.tax_year || this.globalService.tax_year,
      scenario: this.filterParams.scenario || this.globalService.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key
    };

    // Validation for CREATE mode
    if (this.isCreate) {
      if (!this.entity || !this.entity.KEY || !this.sicCode || !this.sicCode.CODE_KEY ||
          !this.adjustmentName || this.adjustmentName === '' ||
          this.adjustmentAmount === undefined || this.adjustmentAmount === null || this.adjustmentAmount === '') {
        errorInfo = "Please select all required fields";
        this._snackBar.open(errorInfo, 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }

      if (parseFloat(this.adjustmentAmount) === 0) {
        errorInfo = "Adjustment amount should not be 0";
        this._snackBar.open(errorInfo, 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }

      // Prepare CREATE data
      returnObj = {
        ENTITY: this.entity.KEY,
        COMBINATION_KEY: this.entity.KEY,
        SIC_CODE: this.sicCode.CODE_KEY,
        IS_SINGLE: 'S',
        TRANS_TYPE: 'RND_EXPENSE_ADJ',
        ADJUSTMENT_NAME: this.adjustmentName,
        AMOUNT: this.adjustmentAmount,
        COMMENTS: this.comments,
        OPERATION_TYPE: 'I'
      };
      transactionDetails.push(returnObj);
    } else {
      // EDIT mode - validate and prepare data
      const hasChanges = this.rdExpAdjDetails.some(detail => 
        detail['operation-type'] === 'D' || detail['operation-type'] === 'U'
      );

      if (!hasChanges) {
        this._snackBar.open('No changes to save', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        this.isSaveClicked = false;
        this.crudLoading = false;
        return;
      }

      // Validate edit mode data
      for (const details of this.rdExpAdjDetails) {
        if (details['operation-type'] === 'D' || details['operation-type'] === 'U') {
          if (!details['ADJ_NAME'] || details['ADJ_NAME'] === '' ||
              details['AMOUNT'] === undefined || details['AMOUNT'] === null || details['AMOUNT'] === '') {
            errorInfo = "Please fill all required fields";
            this._snackBar.open(errorInfo, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.isSaveClicked = false;
            this.crudLoading = false;
            return;
          }

          const newObject: any = {
            COMBINATION_KEY: this.rowData.COMBINATION_KEY,
            SIC_CODE: this.rowData.SIC_CODE_KEY,
            ADJUSTMENT_KEY: details.ADJ_KEY,
            GROUP_KEY: details.ADJ_KEY,
            COMMENTS: details.COMMENTS,
            ADJUSTMENT_NAME: details.ADJ_NAME,
            AMOUNT: details.AMOUNT,
            OPERATION_TYPE: details['operation-type'],
            TRANS_TYPE: 'RND_EXPENSE_ADJ'
          };
          returnObj.IS_SINGLE = 'S';
          transactionDetails.push(newObject);
        }
      }
    }

    const lockObj = this.isCreate ? [{combination_key: this.entity.KEY}] : [];

    this.rdExpContributedService.saveRdExpContributed(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      transactionDetails,
      otherInfo,
      lockObj,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('R & D Expense Adjustment has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset() {
    if (this.isCreate) {
      // CREATE mode: Clear form fields (entities already loaded, no API call needed)
      this.entity = undefined;
      this.sicCode = undefined;
      this.adjustmentName = '';
      this.adjustmentAmount = '';
      this.comments = '';
    } else {
      // EDIT mode: Restore from cached original data (no API call needed)
      if (this.originalRdExpAdjDetails && this.originalRdExpAdjDetails.length > 0) {
        this.rdExpAdjDetails = JSON.parse(JSON.stringify(this.originalRdExpAdjDetails));
        this.updateTotal();
      } else {
        this.loadEditData();
      }
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  compareByKey(item1: any, item2: any): boolean {
    // Handle undefined/null values for "Select..." option
    if (item1 === undefined && item2 === undefined) return true;
    if (item1 === null && item2 === null) return true;
    if (!item1 || !item2) return false;
    return item1.KEY === item2.KEY;
  }

  compareByCodeKey(item1: any, item2: any): boolean {
    // Handle undefined/null values for "Select..." option
    if (item1 === undefined && item2 === undefined) return true;
    if (item1 === null && item2 === null) return true;
    if (!item1 || !item2) return false;
    return item1.CODE_KEY === item2.CODE_KEY;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}

